import { Button, Flex, Group, Modal, SegmentedControl } from "@mantine/core"
import React, { useMemo, useState } from "react"

import { CampaignTypeSelect } from "@/pages/campaign-form/components/core/CampaignTypeSelect/CampaignTypeSelect"
import { CampaignActionCardSelect, MIN_SELECTION } from "@/components/CampaignActionCardSelect"
import { CampaignActionCardTracker } from "@/components/CampaignActionCardTracker"
import { useStyles } from "@/components/CampaignTypeModal/CampaignTypeModal.styles"

interface CampaignTypeModalProps {
    onNext: (campaign_type: string, childrenCampaign: number[]) => void | Promise<void>
    opened: boolean
    close: () => void
}
const { CampaignType } = DjangIO.app.grassroots.campaign.types

const ACTION_OPTIONS = ["Single Action", "Multi-Action"] as const

const MULTI_ACTION_CAMPAIGN_DESCRIPTION = "Select a campaign to run for the organization. Choose up to 3 actions."
const SINGLE_ACTION_CAMPAIGN_DESCRIPTION = "Select a campaign to run for the organization."

export const CampaignTypeModal = ({ onNext, close, opened }: CampaignTypeModalProps) => {
    const [selection, setSelection] = useState<string | null>(null)
    const [selectedCampaignActionCards, setSelectedCampaignActionCards] = useState<number[]>([])
    const [action, setAction] = useState<(typeof ACTION_OPTIONS)[number]>(ACTION_OPTIONS[0])

    const isNextDisabled = useMemo<boolean>(() => {
        const isCampaignTypeNotSelected = selection === null

        const isMultiAction = selection === CampaignType.multi_action.key
        const minSelectionNotReached = selectedCampaignActionCards.length < MIN_SELECTION
        return isCampaignTypeNotSelected || (isMultiAction && minSelectionNotReached)
    }, [selection, selectedCampaignActionCards])

    const { classes } = useStyles({ isNextDisabled })

    const getActionSegmentedControlValues = () => {
        return [
            {
                label: ACTION_OPTIONS[0],
                value: ACTION_OPTIONS[0],
            },
            {
                label: (
                    <div className={classes.badgeWrapper} data-cy="campaign-modal-multi-action-selector">
                        <span>{ACTION_OPTIONS[1]}</span>
                        <span className={classes.newBadge}>NEW</span>
                    </div>
                ),
                value: ACTION_OPTIONS[1],
            },
        ]
    }

    const isMulti = action === ACTION_OPTIONS[1]

    return (
        <Modal.Root
            opened={opened}
            onClose={close}
            centered
            size={"80%"}
            closeOnClickOutside={false}
            className={classes.modalContainer}
        >
            <Modal.Overlay />
            <Modal.Content className={classes.modalContent}>
                <Modal.Header className={classes.modalHeader}>
                    <Modal.Title className={classes.modalTitle}>New Campaign</Modal.Title>
                    <Modal.CloseButton className={classes.modalCloseButton} aria-label="Cancel" size="md" />
                </Modal.Header>
                <Modal.Body className={classes.modalBody}>
                    <Flex className={classes.modalLabel}>
                        <p className={classes.modalLabelText}>
                            {isMulti ? MULTI_ACTION_CAMPAIGN_DESCRIPTION : SINGLE_ACTION_CAMPAIGN_DESCRIPTION}
                        </p>
                        <SegmentedControl
                            classNames={{ label: classes.segmentedControlLabel }}
                            data={getActionSegmentedControlValues()}
                            onChange={(value: (typeof ACTION_OPTIONS)[number]) => {
                                setAction(value)
                                if (value === ACTION_OPTIONS[0]) return setSelection(null)
                                if (value === ACTION_OPTIONS[1]) {
                                    setSelectedCampaignActionCards([])
                                    return setSelection(CampaignType.multi_action.key)
                                }
                            }}
                            value={action}
                        />
                    </Flex>
                    {isMulti ? (
                        <>
                            <CampaignActionCardTracker selectedCampaignTypeValues={selectedCampaignActionCards} />
                            <CampaignActionCardSelect
                                value={selectedCampaignActionCards}
                                onChange={setSelectedCampaignActionCards}
                            />
                        </>
                    ) : (
                        <CampaignTypeSelect selectedCampaignType={selection} setSelectedCampaignType={setSelection} />
                    )}
                </Modal.Body>
                <Group className={classes.modalButtons}>
                    <Button onClick={close} variant="outline" className={classes.cancelButton}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => onNext(selection, selectedCampaignActionCards)}
                        data-cy="campaign-modal-next-button"
                        className={classes.nextButton}
                        disabled={isNextDisabled}
                    >
                        Next
                    </Button>
                </Group>
            </Modal.Content>
        </Modal.Root>
    )
}
